import { useState } from 'react'
import {
  LayoutChangeEvent,
  StyleSheet,
  useWindowDimensions,
} from 'react-native'
import Animated from 'react-native-reanimated'
import Svg, { Defs, LinearGradient, Mask, Rect, Stop } from 'react-native-svg'
import { colors } from '@ui/colors'
import { useAnimatedOpacityOnly } from '@ui/animated/useAnimatedOpacity'

import {
  REVIEW_MASK_BORDER_RADIUS,
  REVIEW_MASK_GRADIENT_OPACITY_BOTTOM,
  REVIEW_MASK_GRADIENT_OPACITY_MIDDLE,
  REVIEW_MASK_GRADIENT_OPACITY_TOP,
  REVIEW_MASK_PADDING,
} from '../styles'

type Size = {
  width: number
  height: number
}

type Props = {
  /** Fades the mask in/out. */
  isVisible?: boolean
}

export const MASK_HEIGHT_PORTRAIT = 320
export const MASK_HEIGHT_LANDSCAPE = 240
/**
 * Fullscreen dimming layer for the review stage: dims everything outside
 * a rounded rectangle at the top of the screen, keeping the zoomed-in
 * face visible. Renders at the size of its parent — mount it absolutely
 * over the camera feed.
 */
export default function ReviewMask({ isVisible = true }: Props) {
  const [size, setSize] = useState<Size | null>(null)
  const styleOpacity = useAnimatedOpacityOnly(isVisible)

  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout
    setSize({ width, height })
  }

  const maskHeight = isLandscape ? MASK_HEIGHT_LANDSCAPE : MASK_HEIGHT_PORTRAIT

  return (
    <Animated.View
      style={[StyleSheet.absoluteFill, styleOpacity]}
      onLayout={handleLayout}
      pointerEvents="none"
    >
      {size && (
        <Svg
          width={size.width}
          height={size.height}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        >
          <Defs>
            <LinearGradient
              id="review-mask-gradient"
              x1="0"
              y1="0"
              x2="0"
              y2={size.height}
              gradientUnits="userSpaceOnUse"
            >
              <Stop
                offset="0"
                stopColor={colors.black}
                stopOpacity={REVIEW_MASK_GRADIENT_OPACITY_TOP}
              />
              <Stop
                offset="0.5"
                stopColor={colors.black}
                stopOpacity={REVIEW_MASK_GRADIENT_OPACITY_MIDDLE}
              />
              <Stop
                offset="1"
                stopColor={colors.black}
                stopOpacity={REVIEW_MASK_GRADIENT_OPACITY_BOTTOM}
              />
            </LinearGradient>
            <Mask
              id="review-mask-hole"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={size.width}
              height={size.height}
            >
              <Rect
                x="0"
                y="0"
                width={size.width}
                height={size.height}
                fill={colors.white}
              />
              <Rect
                x={REVIEW_MASK_PADDING}
                y={REVIEW_MASK_PADDING}
                width={size.width - 2 * REVIEW_MASK_PADDING}
                height={maskHeight}
                rx={REVIEW_MASK_BORDER_RADIUS}
                fill={colors.black}
              />
            </Mask>
          </Defs>
          <Rect
            x="0"
            y="0"
            width={size.width}
            height={size.height}
            fill="url(#review-mask-gradient)"
            mask="url(#review-mask-hole)"
          />
        </Svg>
      )}
    </Animated.View>
  )
}
